import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Send, CheckCircle2, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntakeForm() {
  const { user } = useAuth();
  const [createdMatter, setCreatedMatter] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    matter_name: '',
    client_name: user?.name || '',
    client_email: user?.email || '',
    phone: '',
    case_type: '',
    urgency: 'normal',
    matter_description: '',
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/matters`, form, {
        withCredentials: true,
      });
      setCreatedMatter(res.data);
      toast.success('Matter created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start intake');
    } finally {
      setSubmitting(false);
    }
  };

  if (createdMatter) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center p-6" data-testid="intake-success">
        <div className="max-w-xl w-full text-center space-y-6 animate-fade-in-up">
          <div className="w-16 h-16 mx-auto bg-[#0B1F3F] rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-[#C5A55A]" />
          </div>
          <h1 className="font-heading text-3xl font-semibold text-[#0B1F3F]">Matter Created</h1>
          <p className="text-[#64748B] text-sm leading-relaxed">
            Intake has been submitted under your account. Continue to the matter upload page to open the secure Sync intake portal for this matter.
          </p>
          <div className="bg-white border border-[#D4D4D0] rounded-sm p-5 text-left space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Matter</p>
            <p className="text-[#0B1F3F] font-medium">{createdMatter.matter_name}</p>
            <p className="text-xs text-[#94A3B8]">{createdMatter.matter_reference}</p>
          </div>
          <p className="text-xs text-[#94A3B8]">
            All file submissions are initiated through a secure Sync intake portal. Sensitive records should not be sent by ordinary email.
          </p>
          <div className="flex gap-3 justify-center pt-2">
            <Button asChild className="btn-primary-varfacts px-6 py-3 rounded-sm">
              <Link to={`/matters/${createdMatter.matter_id}/upload?source=intake`}>
                <Shield className="w-4 h-4 mr-2" />
                Continue to Secure Upload
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-secondary-varfacts px-6 py-3 rounded-sm">
              <Link to="/dashboard/matters">View Matters</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="intake-form-page">
      <header className="bg-white border-b border-[#D4D4D0]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/10_VARfacts_logo_header_400x100.png" alt="VARfacts" className="h-8" />
          </Link>
          <Link to="/" className="text-sm text-[#64748B] hover:text-[#0B1F3F] flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10 space-y-3">
          <h1 className="font-heading text-4xl font-semibold text-[#0B1F3F]" data-testid="intake-form-title">
            Start Intake
          </h1>
          <div className="w-16 h-0.5 bg-[#C5A55A]" />
          <p className="text-[#64748B] max-w-2xl leading-relaxed">
            Intake begins inside your account with a brief matter description. After intake submission, the matter is created under your account and the secure Sync upload path is opened for that specific matter.
          </p>
          <p className="text-[#64748B] max-w-2xl leading-relaxed text-sm">
            All file submissions are initiated through a secure Sync intake portal. After intake review, the matter is assigned and handled under the appropriate matter structure.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-5">
            <h3 className="font-heading text-lg font-semibold text-[#0B1F3F]">Matter Setup</h3>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Matter Name *</Label>
              <Input
                value={form.matter_name}
                onChange={(e) => handleChange('matter_name', e.target.value)}
                className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F]"
                placeholder="Example: Walker Disclosure Review"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
                Brief Matter Description *
              </Label>
              <Textarea
                data-testid="intake-matter-description"
                value={form.matter_description}
                onChange={(e) => handleChange('matter_description', e.target.value)}
                placeholder="Provide a brief description of the matter, asserted claims, and record set to be reviewed..."
                className="min-h-[160px] bg-white border-[#D4D4D0] focus:border-[#0B1F3F]"
                required
              />
            </div>
          </div>

          <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-5">
            <h3 className="font-heading text-lg font-semibold text-[#0B1F3F]">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Full Name *</Label>
                <Input
                  data-testid="intake-client-name"
                  value={form.client_name}
                  onChange={(e) => handleChange('client_name', e.target.value)}
                  className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Email Address *</Label>
                <Input
                  data-testid="intake-client-email"
                  type="email"
                  value={form.client_email}
                  onChange={(e) => handleChange('client_email', e.target.value)}
                  className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Phone</Label>
                <Input
                  data-testid="intake-phone"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Case Type</Label>
                <Select value={form.case_type} onValueChange={(val) => handleChange('case_type', val)}>
                  <SelectTrigger data-testid="intake-case-type" className="h-12 bg-white border-[#D4D4D0]">
                    <SelectValue placeholder="Select case type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discovery">Discovery &amp; Disclosure</SelectItem>
                    <SelectItem value="compulsion">Compulsion</SelectItem>
                    <SelectItem value="settlement">Settlement Support</SelectItem>
                    <SelectItem value="counsel_review">Counsel Review</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-5">
            <h3 className="font-heading text-lg font-semibold text-[#0B1F3F]">Matter Priority</h3>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Urgency</Label>
              <Select value={form.urgency} onValueChange={(val) => handleChange('urgency', val)}>
                <SelectTrigger data-testid="intake-urgency" className="h-12 bg-white border-[#D4D4D0] max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Button
              data-testid="intake-submit-btn"
              type="submit"
              disabled={submitting}
              className="btn-primary-varfacts px-8 py-3 rounded-sm h-12"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? 'Submitting...' : 'Create Matter'}
            </Button>
            <p className="text-xs text-[#94A3B8]">
              Documentation triage and technical review only. Not legal, tax, or investment advice. No custody.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
